// ProductNewsReader — khung đọc MỘT bài Tin mới, dạng popup phủ giữa màn hình.
//
// Mở từ: dòng trong chuông, thẻ bài trong chat (kernel openEntityLink → opener 'productNews'),
// trang /whats-new, hoặc link có `?newsId=`. Popup chứ không chuyển trang: người đang ở giữa
// việc (đang chat, đang sửa đơn) đọc xong bấm đóng là về đúng chỗ cũ.
//
// Mở là tính đã đọc — đánh dấu ngay khi nạp xong nội dung, không bắt người đọc bấm thêm nút.
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertTriangle } from 'lucide-react';
import ProductNewsArticle from './ProductNewsArticle';
import { fetchNews } from './productNewsApi';
import { markProductNewsRead } from './productNewsStore';

import { useLingui } from '@lingui/react/macro';

// Bài đã nạp trong phiên — mở lại (từ chuông rồi từ thẻ chat) không phải chờ lần hai.
const CACHE = new Map();

/**
 * Nạp một bài theo id (dùng chung cho popup và trang /whats-new/:id).
 * @returns {{news: object|null, loading: boolean, notFound: boolean}}
 */
export function useProductNewsDetail(id, preview = null) {
  const [state, setState] = useState(() => ({
    news: (id && CACHE.get(id)) || preview || null,
    loading: !!id && !CACHE.has(id),
    notFound: false,
  }));
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    if (!id) return undefined;
    const cached = CACHE.get(id) || null;
    setState({ news: cached || preview || null, loading: !cached, notFound: false });
    fetchNews(id)
      .then((d) => {
        if (!alive.current) return;
        if (d && d.id) {
          CACHE.set(id, d);
          setState({ news: d, loading: false, notFound: false });
          markProductNewsRead(id);
        } else {
          setState({ news: null, loading: false, notFound: true });
        }
      })
      .catch((e) => {
        if (!alive.current) return;
        // 404 = bài đã gỡ / không đăng cho hệ này ⇒ nói thẳng. Lỗi mạng mà đang có bản trong bộ
        // nhớ (hoặc bản tóm tắt) thì vẫn cho đọc phần đang có.
        if (e?.response?.status === 404) { CACHE.delete(id); setState({ news: null, loading: false, notFound: true }); return; }
        setState((s) => ({ news: s.news, loading: false, notFound: !s.news }));
      });
    return () => { alive.current = false; };
    // `preview` chỉ là bản vẽ tạm lúc mở — đổi nó không phải lý do nạp lại.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return state;
}

/**
 * @param {string}   props.id
 * @param {object}   props.preview    bản tóm tắt đã có (vẽ ngay trong lúc chờ)
 * @param {Function} props.onClose
 * @param {Function} props.onOpenApp  (path) ⇒ đóng popup rồi điều hướng tới trang tính năng
 */
export default function ProductNewsReader({ id, preview = null, onClose, onOpenApp }) {
  const { t } = useLingui();
  const { news, loading, notFound } = useProductNewsDetail(id, preview);

  const close = useCallback(() => onClose?.(), [onClose]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [close]);

  const hasCover = !!news?.cover_image_url;

  return createPortal(
    <div className="fixed inset-0 z-[10010] flex items-start sm:items-center justify-center sm:p-6">
      <div className="absolute inset-0 bg-slate-900/60" onClick={close} />
      <div role="dialog" aria-modal="true" aria-label={news?.title || t`Tin mới`}
           className="relative w-full sm:max-w-3xl h-full sm:h-auto sm:max-h-[90vh] bg-white sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        <button type="button" onClick={close} aria-label={t`Đóng`}
                className={`absolute top-3 right-3 z-10 w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
                  hasCover ? 'bg-black/40 text-white hover:bg-black/60' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          <X size={18} />
        </button>

        <div className="overflow-y-auto pb-8">
          {notFound ? (
            <div className="px-6 py-16 text-center">
              <AlertTriangle size={28} className="mx-auto text-amber-500" />
              <p className="mt-3 text-sm text-gray-600">{t`Bài viết này không còn hiển thị (đã gỡ hoặc không dành cho hệ thống này).`}</p>
            </div>
          ) : (
            <div className={hasCover ? '' : 'pt-12'}>
              <ProductNewsArticle
                news={news}
                loading={loading}
                coverClassName="rounded-none"
                bodyClassName="px-6 sm:px-10"
                onNavigate={close}
                onOpenApp={(p) => onOpenApp?.(p)}
              />
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
